import { getSupabaseClient } from '../supabase-client';
import { readSupabaseEnv } from '../../lib/env';
import { reportError } from '../../lib/monitoring';
import type { CaptureStatus } from '../database.types';

/**
 * Confirming ONE Sol proposal. M11 / S11-01.
 *
 * **THE REQUEST CARRIES A TICKET AND NOTHING ELSE.** No Capture id, no status,
 * no Project, no "confirm anyway". The server refuses any other field rather
 * than ignoring it, so there is nothing here for a tampered page to change:
 * what gets written is read out of the signature, not out of this body.
 *
 * **A SEPARATE ENDPOINT FROM `sol-ask`, AND THAT IS THE POINT.** The function
 * that talks to a model cannot write, and the function that writes never sees
 * a model. Sol proposes; a person confirms; the writer executes.
 *
 * **NO RETRY LIVES HERE.** Retry is a tap. A browser loop against a writer is
 * how one confirmation becomes four Work Log events — except it would not even
 * do that, because the writer is idempotent; it would simply be four requests
 * nobody asked for.
 */

export const SOL_CONFIRM_FUNCTION_NAME = 'sol-confirm';

/**
 * What the confirmation did.
 *
 * **`expired` AND `capture_changed` ARE DIFFERENT, AND BOTH ARE HONEST.** A
 * proposal that timed out can be asked for again; a Capture that moved means
 * the screen is out of date and the answer is to look again. Collapsing them
 * into "try again" would give the wrong advice half the time.
 */
export type SolConfirmOutcome =
  | { readonly status: 'confirmed'; readonly captureId: string; readonly captureStatus: string }
  /** A confirmed MY DAY action (WD-2) — the row the DATABASE returned. */
  | {
      readonly status: 'confirmed_my_day';
      readonly action: string;
      readonly item: {
        readonly id: string;
        readonly title: string;
        readonly state: string;
        readonly dueDate: string;
        readonly dueTime: string | null;
      };
    }
  | { readonly status: 'capture_changed' }
  /** The follow-up moved between the proposal and the tap (WD-2). */
  | { readonly status: 'follow_up_changed' }
  | { readonly status: 'expired' }
  | { readonly status: 'signed_out' }
  | { readonly status: 'unavailable' }
  | { readonly status: 'aborted' };

export interface SolConfirmRequest {
  readonly ticket: string;
  readonly signal?: AbortSignal;
}

function isAbort(caught: unknown): boolean {
  return caught instanceof Error && caught.name === 'AbortError';
}

export async function confirmSolAction(request: SolConfirmRequest): Promise<SolConfirmOutcome> {
  const env = readSupabaseEnv();
  const client = getSupabaseClient();
  if (!env || !client) return { status: 'unavailable' };

  try {
    const { data, error } = await client.auth.getSession();
    if (error) return { status: 'unavailable' };
    const accessToken = data.session?.access_token;
    if (!accessToken) return { status: 'signed_out' };

    const response = await fetch(
      `${env.url.replace(/\/+$/, '')}/functions/v1/${SOL_CONFIRM_FUNCTION_NAME}`,
      {
        method: 'POST',
        headers: {
          apikey: env.anonKey,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ticket: request.ticket }),
        ...(request.signal ? { signal: request.signal } : {}),
      },
    );

    if (response.status === 401) return { status: 'signed_out' };
    // The server answers every refusal of the ticket itself identically, so
    // this is "that proposal is no longer good" and nothing more specific.
    if (response.status === 403) return { status: 'expired' };
    if (response.status === 409) {
      // Which record family moved is in the body's own code (WD-2) — the two
      // give Brandon different honest advice.
      const conflict = (await response.json().catch(() => null)) as {
        error?: { code?: unknown };
      } | null;
      return conflict?.error?.code === 'follow_up_changed'
        ? { status: 'follow_up_changed' }
        : { status: 'capture_changed' };
    }
    if (!response.ok) return { status: 'unavailable' };

    const payload = (await response.json()) as {
      result?: unknown;
      captureId?: unknown;
      status?: unknown;
      action?: unknown;
      item?: unknown;
    };
    // **SHAPE IS CHECKED, NOT ASSUMED.** Reporting a change that the response
    // does not actually describe would be the one lie this whole path exists
    // to prevent.
    if (payload.result !== 'confirmed') return { status: 'unavailable' };
    if (typeof payload.action === 'string') {
      const item = payload.item as {
        id?: unknown;
        title?: unknown;
        state?: unknown;
        dueDate?: unknown;
        dueTime?: unknown;
      } | null;
      if (
        item === null ||
        typeof item !== 'object' ||
        typeof item.id !== 'string' ||
        typeof item.title !== 'string' ||
        typeof item.state !== 'string' ||
        typeof item.dueDate !== 'string' ||
        (item.dueTime !== null && typeof item.dueTime !== 'string')
      ) {
        return { status: 'unavailable' };
      }
      return {
        status: 'confirmed_my_day',
        action: payload.action,
        item: {
          id: item.id,
          title: item.title,
          state: item.state,
          dueDate: item.dueDate,
          dueTime: item.dueTime,
        },
      };
    }
    if (typeof payload.captureId !== 'string' || typeof payload.status !== 'string') {
      return { status: 'unavailable' };
    }
    return {
      status: 'confirmed',
      captureId: payload.captureId,
      captureStatus: payload.status,
    };
  } catch (caught) {
    if (isAbort(caught)) return { status: 'aborted' };
    reportError(caught, 'sol.confirm');
    return { status: 'unavailable' };
  }
}

/** Narrowing helper for the card, which speaks in `CaptureStatus`. */
export function asCaptureStatus(value: string): CaptureStatus | null {
  return value === 'open' || value === 'repaired' || value === 'closed' ? value : null;
}
